/**
 * Pipeline (Track Builder) config — the structured per-game data the SkinResolver consumes.
 * See data/frogger.config.js for the shape rationale.
 *
 * `levels` is a small progression table. Phase 2's OptionsScene will let designers
 * tweak countdown / queue / speed / station count per ship.
 */

import {
    PIPELINE_CONFIG,
    PIPELINE_GRID,
    TILE_DEFS,
    TILE_WEIGHTS,
} from '../src/games/pipeline/pipelineConfig.js';

export const PIPELINE = {
    grid: {
        cols: PIPELINE_GRID.cols,
        rows: PIPELINE_GRID.rows,
        cellSize: PIPELINE_GRID.cellSize,
        originX: PIPELINE_GRID.originX,
        originY: PIPELINE_GRID.originY,
        targetCellSize: PIPELINE_CONFIG.TARGET_CELL_SIZE,
        paddingTop: PIPELINE_CONFIG.PADDING_TOP,
        paddingSide: PIPELINE_CONFIG.PADDING_SIDE,
        paddingBottom: PIPELINE_CONFIG.PADDING_BOTTOM,
    },

    tuning: {
        countdownSeconds: PIPELINE_CONFIG.COUNTDOWN_SECONDS,
        trainBaseSpeed: PIPELINE_CONFIG.TRAIN_BASE_SPEED,
        trainSpeedIncrement: PIPELINE_CONFIG.TRAIN_SPEED_INCREMENT,
        trainMaxSpeed: PIPELINE_CONFIG.TRAIN_MAX_SPEED,
        levelSpeedMultiplier: 0.08,
        pointsPerTile: PIPELINE_CONFIG.POINTS_PER_TILE,
        pointsCrossBonus: PIPELINE_CONFIG.POINTS_CROSS_BONUS,
        stationPoints: PIPELINE_CONFIG.STATION_POINTS,
        endBonus: PIPELINE_CONFIG.END_BONUS,
        queueSize: PIPELINE_CONFIG.QUEUE_SIZE,
        obstacleShapes: PIPELINE_CONFIG.OBSTACLE_SHAPES,
        singleBlockChance: PIPELINE_CONFIG.SINGLE_BLOCK_CHANCE,
        protectedMargin: PIPELINE_CONFIG.PROTECTED_MARGIN,
        stationCountBase: PIPELINE_CONFIG.STATION_COUNT_BASE,
        stationCountPerLevel: PIPELINE_CONFIG.STATION_COUNT_PER_LEVEL,
        stationCountCap: PIPELINE_CONFIG.STATION_COUNT_CAP,
        tileDefs: TILE_DEFS,
        tileWeights: TILE_WEIGHTS,
    },

    modes: {
        normal: { label: 'Standard' },
    },

    settings: {
        countdownSeconds:   { default: PIPELINE_CONFIG.COUNTDOWN_SECONDS, type: 'number', min: 5, max: 60, step: 1,    label: 'Countdown (s)' },
        queueSize:          { default: PIPELINE_CONFIG.QUEUE_SIZE,        type: 'number', min: 3, max: 10, step: 1,    label: 'Queue Depth' },
        trainSpeed:         { default: 'normal',                          type: 'choice', options: ['slow', 'normal', 'fast'], label: 'Train Speed' },
        startDirection:     { default: 'east',                            type: 'choice', options: ['north', 'east', 'south', 'west'], label: 'Train Start Direction' },
    },

    levels: [
        { id: 1, label: 'Level 1', stationCount: 4,  trainSpeed: PIPELINE_CONFIG.TRAIN_BASE_SPEED },
        { id: 2, label: 'Level 2', stationCount: 5,  trainSpeed: PIPELINE_CONFIG.TRAIN_BASE_SPEED + PIPELINE_CONFIG.TRAIN_SPEED_INCREMENT * 8 },
        { id: 3, label: 'Level 3', stationCount: 6,  trainSpeed: PIPELINE_CONFIG.TRAIN_BASE_SPEED + PIPELINE_CONFIG.TRAIN_SPEED_INCREMENT * 16 },
        { id: 4, label: 'Level 4', stationCount: 7,  trainSpeed: PIPELINE_CONFIG.TRAIN_BASE_SPEED + PIPELINE_CONFIG.TRAIN_SPEED_INCREMENT * 24 },
        { id: 5, label: 'Level 5', stationCount: 8,  trainSpeed: PIPELINE_CONFIG.TRAIN_BASE_SPEED + PIPELINE_CONFIG.TRAIN_SPEED_INCREMENT * 32 },
    ],

    elements: {
        'atlas.main':       { label: 'Shared Atlas Source', category: 'atlas',      footprint: { w: 1, h: 1 } },
        background:         { label: 'Background',          category: 'board',      fitMode: 'cover' },
        'board.cell':       { label: 'Board Cell Texture',  category: 'board',      footprint: { w: 1, h: 1 } },
        'board.border':     { label: 'Board Border',        category: 'board',      fitMode: 'cover' },
        'track.straight':   { label: 'Track Straight',      category: 'board',      footprint: { w: 1, h: 1 } },
        'track.corner':     { label: 'Track Corner',        category: 'board',      footprint: { w: 1, h: 1 } },
        'track.t':          { label: 'Track T Junction',    category: 'board',      footprint: { w: 1, h: 1 } },
        'track.cross':      { label: 'Track Cross',         category: 'board',      footprint: { w: 1, h: 1 } },
        'track.deadend':    { label: 'Track Dead End',      category: 'board',      footprint: { w: 1, h: 1 } },
        'tram.head':        { label: 'Tram First Carriage', category: 'characters', footprint: { w: 2, h: 1 }, fallbackColor: PIPELINE_CONFIG.COLOR_TRAIN },
        'tram.body':        { label: 'Tram Other Carriage', category: 'characters', footprint: { w: 2, h: 1 }, fallbackColor: PIPELINE_CONFIG.COLOR_TRAIN },
        'tram.connector':   { label: 'Tram Connector',      category: 'characters', footprint: { w: 1, h: 1 } },
        station:            { label: 'Station Marker',      category: 'goal',       footprint: { w: 1, h: 1 }, fallbackColor: PIPELINE_CONFIG.COLOR_STATION },
        goal:               { label: 'Goal / House',        category: 'goal',       footprint: { w: 2, h: 2 }, fallbackColor: PIPELINE_CONFIG.COLOR_END },
        start:              { label: 'Start Marker',        category: 'goal',       footprint: { w: 1, h: 1 }, fallbackColor: PIPELINE_CONFIG.COLOR_START },
        blocked:            { label: 'Blocked Tile',        category: 'obstacles',  footprint: { w: 1, h: 1 }, fallbackColor: PIPELINE_CONFIG.COLOR_BLOCKED },
        'hud.panel':        { label: 'HUD Panel',           category: 'ui' },
    },

    palette: {
        gridBg: PIPELINE_CONFIG.COLOR_GRID_BG,
        gridLine: PIPELINE_CONFIG.COLOR_GRID_LINE,
        tileBg: PIPELINE_CONFIG.COLOR_TILE_BG,
        start: PIPELINE_CONFIG.COLOR_START,
        end: PIPELINE_CONFIG.COLOR_END,
        station: PIPELINE_CONFIG.COLOR_STATION,
        train: PIPELINE_CONFIG.COLOR_TRAIN,
        blocked: PIPELINE_CONFIG.COLOR_BLOCKED,
    },
};
